import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MessageCircle, Lock, ShoppingBag } from "lucide-react";
import { BrandMark, AirplaneLogo } from "@/components/motifs";
import { logoFor } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

export default function Footer() {
  const { settings } = useStore();
  const wa = settings?.whatsapp_number || "919999999999";

  return (
    <footer className="relative mt-10 overflow-hidden bg-[#2C221E] text-stone-300 grain">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <BrandMark src={logoFor(settings, "light")} className="[&_*]:!text-[#F4E7C5]" />
            <p className="mt-5 max-w-sm font-sans text-sm leading-relaxed text-stone-400">
              Fine anti-tarnish & oxidised jewellery, delivered in our signature golden passport flight box. Every
              parcel is sent as a registered first-class flight to your doorstep.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <Link
                to="/shop"
                data-testid="footer-shop-now"
                className="flex items-center gap-2 rounded-full gold-gradient px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-stone-900 transition-transform hover:scale-105"
              >
                <ShoppingBag size={16} /> Shop the Collection
              </Link>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <a
                data-testid="footer-instagram"
                href={settings?.instagram_url || "#"}
                target="_blank"
                rel="noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 hover:border-amber-500 hover:text-amber-400 transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                data-testid="footer-whatsapp"
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 hover:border-amber-500 hover:text-amber-400 transition-colors"
                aria-label="WhatsApp"
              >
                <MessageCircle size={18} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500/80">Explore</h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/shop" className="hover:text-amber-400 transition-colors">Shop All</Link></li>
              <li><Link to="/shop?category=Rings" className="hover:text-amber-400 transition-colors">Rings</Link></li>
              <li><Link to="/shop?category=Necklaces" className="hover:text-amber-400 transition-colors">Necklaces</Link></li>
              <li><a href="/#story" className="hover:text-amber-400 transition-colors">Our Story</a></li>
              <li><a href="/#offers" className="hover:text-amber-400 transition-colors">Offers</a></li>
              <li>
                <Link to="/admin" data-testid="footer-admin-link" className="inline-flex items-center gap-1.5 text-amber-400/90 hover:text-amber-300 transition-colors">
                  <Lock size={13} /> Admin Login
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-500/80">Concierge</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-2">
                <Mail size={15} className="text-amber-500/70" />
                <a href={`mailto:${settings?.email}`} className="hover:text-amber-400 transition-colors">
                  {settings?.email || "hello@priyaraa.com"}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={15} className="text-amber-500/70" />
                <span>{settings?.phone || "+91 99999 99999"}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center gap-3 border-t border-white/10 pt-8 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2 opacity-60">
            <AirplaneLogo size={20} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
              priyaraa.com · Boarding since 2026
            </span>
          </div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
            © {new Date().getFullYear()} PRIYARAA. All rights reserved.
          </p>
          <Link to="/admin" data-testid="footer-admin-link-bottom" className="flex items-center gap-1.5 rounded-full border border-amber-600/40 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-400 hover:border-amber-500 hover:text-amber-300 transition-colors">
            <Lock size={12} /> Admin Login
          </Link>
        </div>
      </div>
    </footer>
  );
}
